import type { BuildGraphNode } from "../types/build";

const NODE_WIDTH = 190;
const NODE_HEIGHT = 72;
const COLUMN_GAP = 110;
const ROW_GAP = 36;
const CANVAS_PADDING = 36;

type PositionedBuildGraphNode = BuildGraphNode & {
  x: number;
  y: number;
};

export type BuildGraphLayout = {
  nodes: PositionedBuildGraphNode[];
  edges: Array<{
    source: PositionedBuildGraphNode;
    target: PositionedBuildGraphNode;
  }>;
  width: number;
  height: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(
  record: Record<string, unknown>,
  camelName: string,
  pascalName: string,
) {
  const value = record[camelName] ?? record[pascalName];
  return typeof value === "string" ? value : null;
}

function readBoolean(
  record: Record<string, unknown>,
  camelName: string,
  pascalName: string,
) {
  const value = record[camelName] ?? record[pascalName];
  return typeof value === "boolean" ? value : false;
}

function parseNode(value: unknown): BuildGraphNode | null {
  if (!isRecord(value)) {
    return null;
  }

  const id = readString(value, "id", "Id");
  const projectId = readString(value, "projectId", "ProjectId");
  const projectDir = readString(value, "projectDir", "ProjectDir");
  const target = readString(value, "target", "Target");
  if (!id || !projectId || !projectDir || !target) {
    return null;
  }

  const rawDependencies = value.dependencies ?? value.Dependencies;
  const dependencies = Array.isArray(rawDependencies)
    ? rawDependencies.filter(
        (dependency): dependency is string => typeof dependency === "string",
      )
    : [];

  return {
    id,
    projectId,
    projectName: readString(value, "projectName", "ProjectName"),
    projectDir,
    target,
    phase: readString(value, "phase", "Phase"),
    dependencies,
    artifacts: readString(value, "artifacts", "Artifacts"),
    build: readString(value, "build", "Build"),
    batch: readString(value, "batch", "Batch"),
    action: readString(value, "action", "Action"),
    required: readBoolean(value, "required", "Required"),
    isBatchNode: readBoolean(value, "isBatchNode", "IsBatchNode"),
  };
}

export function parseBuildGraph(graph: unknown) {
  if (!isRecord(graph)) {
    return [];
  }

  const rawNodes = graph.nodes ?? graph.Nodes;
  const values = Array.isArray(rawNodes)
    ? rawNodes
    : isRecord(rawNodes)
      ? Object.values(rawNodes)
      : [];

  return values
    .map(parseNode)
    .filter((node): node is BuildGraphNode => node !== null);
}

export function parseBuildTargets(context: unknown) {
  let parsedContext = context;

  if (typeof context === "string") {
    try {
      parsedContext = JSON.parse(context) as unknown;
    } catch {
      return [];
    }
  }

  if (!isRecord(parsedContext)) {
    return [];
  }

  const rawTargets = parsedContext.targets ?? parsedContext.Targets;
  if (!Array.isArray(rawTargets)) {
    return [];
  }

  return rawTargets.filter(
    (target): target is string => typeof target === "string",
  );
}

export function filterBuildGraphNodes(
  nodes: BuildGraphNode[],
  visibleTargets: ReadonlySet<string>,
  showIgnoredNodes: boolean,
) {
  return nodes.filter(
    (node) =>
      visibleTargets.has(node.target) &&
      (showIgnoredNodes || node.action !== "ignore"),
  );
}

export function layoutBuildGraph(nodes: BuildGraphNode[]): BuildGraphLayout {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const depths = new Map<string, number>();

  const findDepth = (nodeId: string, visiting: Set<string>): number => {
    const knownDepth = depths.get(nodeId);
    if (knownDepth !== undefined) {
      return knownDepth;
    }
    if (visiting.has(nodeId)) {
      return 0;
    }

    const node = nodesById.get(nodeId);
    if (!node) {
      return 0;
    }

    const nextVisiting = new Set(visiting).add(nodeId);
    const dependencyDepths = node.dependencies
      .filter((dependency) => nodesById.has(dependency))
      .map((dependency) => findDepth(dependency, nextVisiting));
    const depth = dependencyDepths.length
      ? Math.max(...dependencyDepths) + 1
      : 0;
    depths.set(nodeId, depth);
    return depth;
  };

  nodes.forEach((node) => findDepth(node.id, new Set()));
  const maxDepth = Math.max(0, ...depths.values());
  const columns = new Map<number, BuildGraphNode[]>();

  nodes.forEach((node) => {
    const depth = depths.get(node.id) ?? 0;
    const column = columns.get(depth) ?? [];
    column.push(node);
    columns.set(depth, column);
  });

  const maxRows = Math.max(1, ...Array.from(columns.values(), (items) => items.length));
  const positionedNodes = Array.from(columns.entries()).flatMap(
    ([depth, columnNodes]) => {
      const sortedNodes = [...columnNodes].sort((left, right) =>
        `${left.projectDir}:${left.target}`.localeCompare(
          `${right.projectDir}:${right.target}`,
        ),
      );
      const columnHeight =
        sortedNodes.length * NODE_HEIGHT +
        Math.max(0, sortedNodes.length - 1) * ROW_GAP;
      const availableHeight =
        maxRows * NODE_HEIGHT + Math.max(0, maxRows - 1) * ROW_GAP;
      const offsetY = (availableHeight - columnHeight) / 2;

      return sortedNodes.map((node, row) => ({
        ...node,
        x:
          CANVAS_PADDING +
          (maxDepth - depth) * (NODE_WIDTH + COLUMN_GAP),
        y: CANVAS_PADDING + offsetY + row * (NODE_HEIGHT + ROW_GAP),
      }));
    },
  );
  const positionedById = new Map(
    positionedNodes.map((node) => [node.id, node]),
  );
  const edges = positionedNodes.flatMap((source) =>
    source.dependencies.flatMap((dependency) => {
      const target = positionedById.get(dependency);
      return target ? [{ source, target }] : [];
    }),
  );

  return {
    nodes: positionedNodes,
    edges,
    width:
      CANVAS_PADDING * 2 +
      (maxDepth + 1) * NODE_WIDTH +
      maxDepth * COLUMN_GAP,
    height:
      CANVAS_PADDING * 2 +
      maxRows * NODE_HEIGHT +
      Math.max(0, maxRows - 1) * ROW_GAP,
  };
}

export const buildGraphNodeSize = {
  width: NODE_WIDTH,
  height: NODE_HEIGHT,
};
